import { Key, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { MdOutlineDelete, MdOutlineEdit, MdOutlineMoreHoriz } from 'react-icons/md';
import { TmdxAvatar } from '@ds';
import { ActionMenu, MenuItemType, TableColumnType } from '@ui';
import { Contact, Hospital, HospitalsContactListFilters } from '@/api/apis/facilities';
import { getContactFullName, getFullName } from '@/utils/helpers';
import { col, COMMON_COLUMN_PRESETS } from '../../utils';

export const HOSPITAL_CONTACT_LIST_FILTERS_BASE: Record<keyof HospitalsContactListFilters, string | string[]> = {
  hospitals: [],
  primary: 'all',
};

export const HOSPITAL_CONTACTS_TABLE_COLUMNS: TableColumnType[] = [
  col('fullName', 'Donor Hospital contact name', {
    sortable: true,
    minWidthClassName: 'min-w-60',
  }),
  col('parentName', 'Donor Hospital name', {
    sortable: true,
    minWidthClassName: 'min-w-80',
  }),
  col('email', 'Email', {
    sortable: true,
    minWidthClassName: 'min-w-60',
  }),
  col('primaryPhone', 'Primary phone', {
    sortable: false,
    minWidthClassName: 'min-w-60',
  }),
  col('secondaryPhone', 'Secondary phone', {
    sortable: false,
    minWidthClassName: 'min-w-60',
  }),
  col('isPrimary', 'Is primary contact', {
    sortable: false,
    minWidthClassName: 'min-w-28',
  }),
  COMMON_COLUMN_PRESETS.actions,
];

const getActionsMenu = (onEdit: () => void, onDelete: () => void): MenuItemType[] => [
  {
    key: 'edit-contact',
    type: 'action',
    to: 'edit-contact',
    label: (
      <div className="flex items-center gap-2">
        <MdOutlineEdit size={16} />
        <span className="text-m-r">Edit</span>
      </div>
    ),
    onAction: () => onEdit(),
  },
  {
    key: 'delete-contact',
    type: 'action',
    to: 'delete-contact',
    label: (
      <div className="flex items-center gap-2 text-danger">
        <MdOutlineDelete size={16} />
        <span className="text-m-r">Delete</span>
      </div>
    ),
    onAction: () => onDelete(),
  },
];

export const getRenderHospitalContactsTableCell =
  (
    onEdit: (hospitalId: string) => void,
    onDelete: (hospitalId: string) => void,
    resolveSubdivisionName: (stateCode?: string | null, countryCode?: string | null) => string | undefined,
  ) =>
  (contact: Contact, columnKey: Key): ReactNode => {
    const { id, firstName, lastName, photoPath, email, parent, isPrimary, primaryPhone, secondaryPhone } = contact;

    const onContactEdit = () => onEdit(id);
    const onContactDelete = () => onDelete(id);

    const ACTIONS_MENU = getActionsMenu(onContactEdit, onContactDelete);

    switch (columnKey) {
      case 'fullName':
        return (
          <Link to={`/admin/hospitals/contacts/${id}`} className="flex items-center gap-2">
            <TmdxAvatar radius="xl" size="xl" name={getFullName(firstName, lastName)} src={photoPath} />
            <span className="text-m-m">{getContactFullName(firstName, lastName)}</span>
          </Link>
        );
      case 'parentName':
        return (
          <div className="flex items-center gap-2">
            <TmdxAvatar
              radius="xl"
              size="xl"
              type="facility"
              fallbackInitials={false}
              name={parent?.code || resolveSubdivisionName(parent?.location?.state, parent?.location?.country)}
              src={(parent as Hospital)?.logoPath}
            />
            <span className="text-m-m">{parent?.name}</span>
          </div>
        );
      case 'email':
        return <span className="text-m-r">{email}</span>;
      case 'primaryPhone':
        return <span className="text-m-r">{primaryPhone}</span>;
      case 'secondaryPhone':
        return <span className="text-m-r">{secondaryPhone}</span>;
      case 'isPrimary':
        return <span className="text-m-r">{isPrimary ? 'Yes' : 'No'}</span>;
      case 'actions':
        return (
          <ActionMenu
            placement="left-start"
            items={ACTIONS_MENU}
            MenuTrigger={
              <div className="">
                <MdOutlineMoreHoriz size={20} />
              </div>
            }
            label="hospital contact actions menu"
          />
        );
      default:
        return null;
    }
  };
